import { StateNodeConfig, TransitionConfigOrTarget } from 'xstate/dist/declarations/src/types';

import { AuthenticatedRoutes, Routes } from '@/router/routes';
import { CommonInternalStates } from '@/router/state/root/component/common-internal-states';
import { RootContext } from '@/router/state/root/component/root-context';
import { RootEvent } from '@/router/state/root/component/root-event';

// Helper function to check if intended route matches dynamic patterns
function matchesDynamicRoutePattern(intendedRoute: string): Routes | undefined {
  // Check for project details route pattern: /projects/:projectId
  if (intendedRoute?.startsWith('/projects/') && intendedRoute !== '/projects') {
    return AuthenticatedRoutes.ProjectDetails;
  }

  // Check for client details route pattern: /clients/:clientId
  if (intendedRoute?.startsWith('/clients/') && intendedRoute !== '/clients') {
    return AuthenticatedRoutes.ClientDetails;
  }

  // Check for team member details route pattern: /team/:memberId
  if (intendedRoute?.startsWith('/team/') && intendedRoute !== '/team') {
    return AuthenticatedRoutes.TeamMemberDetails;
  }

  // Add other dynamic routes here as needed

  return undefined;
}

export function createStatesForRoutes({
  routes,
  defaultRoute,
}: {
  routes: Routes[];
  defaultRoute: Routes;
}): Record<string, RootStateNodeConfig> {
  return {
    [CommonInternalStates.Redirecting]: {
      always: performInitialTransition({ routes, defaultRoute }),
    },
    ...Object.fromEntries(routes.map(route => [route, {}])),
  };
}

/**
 * Performs initial transition for the given routes.
 * The initial route is determined by the `intendedRoute` in the context.
 */
function performInitialTransition({
  routes,
  defaultRoute,
}: {
  routes: Routes[];
  defaultRoute: Routes;
}): RootStateTransitionConfigOrTarget {
  return [
    // Check for exact route matches first
    ...routes
      .filter(route => route !== defaultRoute)
      .map(route => ({
        target: route,
        guard: ({ context }) => {
          return context.intendedRoute === route;
        },
      })),
    // Check for dynamic route patterns
    ...routes
      .filter(route => route !== defaultRoute)
      .map(route => ({
        target: route,
        guard: ({ context }) => {
          const matchedDynamicRoute = matchesDynamicRoutePattern(context.intendedRoute || '');
          return matchedDynamicRoute === route;
        },
      })),
    // Default fallback
    { target: defaultRoute },
  ];
}

type RootStateNodeConfig = StateNodeConfig<
  RootContext,
  RootEvent,
  never, // Actor
  never, // Action
  never, // Guard
  never, // Delay
  never, // Tag
  any, // Output
  never, // Emitted
  never // Meta
>;
type RootStateTransitionConfigOrTarget = TransitionConfigOrTarget<
  RootContext,
  RootEvent,
  RootEvent,
  never, // Actor
  never, // Action
  never, // Guard
  never, // Delay
  never, // Emitted
  never // Meta
>;
